import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Toast from '@/components/ui/Toast';
import { portalProducts, type PortalProduct } from '@/mocks/portal';
import { aed, shortDate } from '@/lib/format';

type Filter = 'All' | 'Published' | 'UnderReview' | 'Discontinued';

const filters: { key: Filter; labelKey: string }[] = [
  { key: 'All', labelKey: 'portal.products.filterAll' },
  { key: 'Published', labelKey: 'portal.products.filterPublished' },
  { key: 'UnderReview', labelKey: 'portal.products.filterReview' },
  { key: 'Discontinued', labelKey: 'portal.products.filterDiscontinued' },
];

export default function PortalProductsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<PortalProduct[]>(portalProducts);
  const [filter, setFilter] = useState<Filter>('All');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [dirty, setDirty] = useState<string[]>([]);
  const [toast, setToast] = useState<{ message: string; detail?: string } | null>(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((p) => {
      const matchesFilter = filter === 'All' || p.status === filter;
      const matchesQuery = q === '' || p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [items, filter, query]);

  const patch = (sku: string, change: Partial<PortalProduct>) => {
    setItems((prev) => prev.map((p) => (p.sku === sku ? { ...p, ...change } : p)));
    setDirty((prev) => (prev.includes(sku) ? prev : [...prev, sku]));
  };

  const toggleSelect = (sku: string) => {
    setSelected((prev) => (prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]));
  };

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.includes(r.sku));

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected((prev) => prev.filter((s) => !rows.some((r) => r.sku === s)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...rows.map((r) => r.sku)])));
    }
  };

  const save = () => {
    const count = dirty.length;
    setDirty([]);
    setToast({ message: t('portal.products.savedToast', { count }) });
  };

  const saveRow = (sku: string) => {
    setDirty((prev) => prev.filter((s) => s !== sku));
    const item = items.find((p) => p.sku === sku);
    setToast({ message: t('portal.products.rowSaved', { sku, count: item?.stock ?? 0 }) });
  };

  const discontinue = () => {
    if (selected.length === 0) return;
    setItems((prev) => prev.map((p) => (selected.includes(p.sku) ? { ...p, status: 'Discontinued' } : p)));
    setToast({ message: t('portal.products.discontinuedToast', { count: selected.length }) });
    setSelected([]);
  };

  const toneOf = (status: string) => status;

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.products.title')}</h1>
          <p className="mt-1 max-w-2xl text-[12.5px] leading-5 text-foreground-500">{t('portal.products.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={discontinue}
            disabled={selected.length === 0}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <i className="ri-archive-line text-[14px]" />
            {t('portal.products.markDiscontinued')}
            {selected.length > 0 && <span className="tnum text-foreground-500">({selected.length})</span>}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={dirty.length === 0}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-background-300 disabled:text-foreground-500"
          >
            <i className="ri-save-3-line text-[14px]" />
            {t('portal.products.save')}
            {dirty.length > 0 && <span className="tnum">({dirty.length})</span>}
          </button>
        </div>
      </div>

      <div className="mb-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
        <p className="flex items-start gap-1.5 text-[11.5px] leading-4 text-primary-900">
          <i className="ri-information-line mt-px text-[13px] text-primary-600" />
          {t('portal.products.hint')}
        </p>
      </div>

      <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-[11.5px] font-medium transition-colors ${
                filter === f.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {t(f.labelKey)}
              {f.key === 'All' && <span className="tnum ml-1 text-foreground-400">{items.length}</span>}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-[260px]">
          <i className="ri-search-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-foreground-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('portal.products.searchPlaceholder')}
            aria-label={t('portal.products.searchPlaceholder')}
            className="h-8 w-full rounded-md border border-background-200 bg-background-50 pl-8 pr-2 text-[12px] outline-none focus:border-primary-400"
          />
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2">
          <p className="text-[11.5px] font-medium text-foreground-700">
            {t('portal.products.selected', { count: selected.length })}
          </p>
          <button
            type="button"
            onClick={() => setSelected([])}
            className="cursor-pointer whitespace-nowrap text-[11px] font-medium text-foreground-500 hover:text-foreground-800"
          >
            {t('portal.common.reset')}
          </button>
        </div>
      )}

      <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:block">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[1080px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="w-9 px-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label={t('portal.products.selectAll')}
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5 cursor-pointer rounded border-background-400 accent-primary-600"
                  />
                </th>
                <th className="px-2 py-2.5 font-medium">{t('portal.products.colItem')}</th>
                <th className="px-2 py-2.5 text-right font-medium">{t('portal.products.colPrice')}</th>
                <th className="px-2 py-2.5 font-medium">{t('portal.products.colStock')}</th>
                <th className="px-2 py-2.5 font-medium">{t('portal.products.colIncoming')}</th>
                <th className="px-2 py-2.5 font-medium">{t('portal.products.colEta')}</th>
                <th className="px-2 py-2.5 font-medium">{t('portal.products.colStatus')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.products.colUpdated')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.sku}
                  className={`border-b border-background-200/70 align-middle last:border-0 ${
                    dirty.includes(p.sku) ? 'bg-accent-50/40' : 'hover:bg-background-100/50'
                  }`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={t('portal.products.selectRow', { sku: p.sku })}
                      checked={selected.includes(p.sku)}
                      onChange={() => toggleSelect(p.sku)}
                      className="h-3.5 w-3.5 cursor-pointer rounded border-background-400 accent-primary-600"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2.5">
                      <span className="block h-9 w-9 shrink-0 overflow-hidden rounded border border-background-200 bg-background-100">
                        <img src={p.thumb} alt={p.name} className="h-full w-full object-top" />
                      </span>
                      <div className="min-w-0">
                        <p className="tnum text-[11px] font-semibold text-primary-700">{p.sku}</p>
                        <p className="max-w-[230px] truncate text-[12px] font-medium text-foreground-900">{p.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="tnum px-2 py-2 text-right font-medium text-foreground-900">{aed(p.price)}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        aria-label="-"
                        onClick={() => patch(p.sku, { stock: Math.max(0, p.stock - 10) })}
                        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-background-300 text-foreground-600 hover:bg-background-100"
                      >
                        <i className="ri-subtract-line text-[12px]" />
                      </button>
                      <input
                        type="number"
                        value={p.stock}
                        onChange={(e) => patch(p.sku, { stock: Math.max(0, Number(e.target.value) || 0) })}
                        aria-label={`${p.sku} ${t('portal.products.stockNow')}`}
                        className="tnum h-6 w-[58px] rounded border border-background-300 bg-background-50 px-1 text-center text-[11.5px] outline-none focus:border-primary-400"
                      />
                      <button
                        type="button"
                        aria-label="+"
                        onClick={() => patch(p.sku, { stock: p.stock + 10 })}
                        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-background-300 text-foreground-600 hover:bg-background-100"
                      >
                        <i className="ri-add-line text-[12px]" />
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      value={p.incoming}
                      onChange={(e) => patch(p.sku, { incoming: Math.max(0, Number(e.target.value) || 0) })}
                      aria-label={`${p.sku} ${t('portal.products.incoming')}`}
                      className="tnum h-6 w-[64px] rounded border border-background-300 bg-background-50 px-1.5 text-[11.5px] outline-none focus:border-primary-400"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="date"
                      value={p.eta}
                      onChange={(e) => patch(p.sku, { eta: e.target.value })}
                      aria-label={`${p.sku} ${t('portal.products.eta')}`}
                      className="h-6 w-[112px] rounded border border-background-300 bg-background-50 px-1 text-[10.5px] text-foreground-700 outline-none focus:border-primary-400"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <PortalStatus status={toneOf(p.status)} />
                  </td>
                  <td className="px-3 py-2">
                    <span className="tnum text-[11px] text-foreground-500">{shortDate(p.updated)}</span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('portal.products.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-background-200 px-3 py-2 text-[10.5px] text-foreground-400">
          <span>{t('portal.common.showing', { shown: rows.length, total: items.length })}</span>
          <span>{t('portal.products.priceNote')}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 lg:hidden">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-400">
          {t('portal.products.mobileNote')}
        </p>
        {rows.map((p) => (
          <div key={p.sku} className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="flex items-start gap-2.5">
              <span className="block h-12 w-12 shrink-0 overflow-hidden rounded border border-background-200 bg-background-100">
                <img src={p.thumb} alt={p.name} className="h-full w-full object-top" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="tnum text-[10.5px] font-semibold text-primary-700">{p.sku}</p>
                    <p className="truncate text-[12.5px] font-medium text-foreground-900">{p.name}</p>
                  </div>
                  <PortalStatus status={p.status} className="!px-1.5 !text-[10px]" />
                </div>
                <p className="tnum mt-1 text-[11px] text-foreground-500">
                  {aed(p.price)} · {t('portal.products.colUpdated')} {shortDate(p.updated)}
                </p>
              </div>
            </div>

            <div className="mt-3 flex items-end gap-2">
              <label className="min-w-0 flex-1">
                <span className="mb-1 block text-[10.5px] font-medium text-foreground-500">
                  {t('portal.products.stockNow')}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="-"
                    onClick={() => patch(p.sku, { stock: Math.max(0, p.stock - 10) })}
                    className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700"
                  >
                    <i className="ri-subtract-line text-[16px]" />
                  </button>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={p.stock}
                    onChange={(e) => patch(p.sku, { stock: Math.max(0, Number(e.target.value) || 0) })}
                    aria-label={`${p.sku} ${t('portal.products.stockNow')}`}
                    className="tnum h-10 min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-2 text-center text-[15px] font-semibold outline-none focus:border-primary-400"
                  />
                  <button
                    type="button"
                    aria-label="+"
                    onClick={() => patch(p.sku, { stock: p.stock + 10 })}
                    className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700"
                  >
                    <i className="ri-add-line text-[16px]" />
                  </button>
                </div>
              </label>
              <button
                type="button"
                onClick={() => saveRow(p.sku)}
                className="h-10 shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50"
              >
                {t('portal.products.saveRow')}
              </button>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="min-w-0">
                <span className="mb-1 block text-[10px] font-medium text-foreground-500">
                  {t('portal.products.incoming')}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  value={p.incoming}
                  onChange={(e) => patch(p.sku, { incoming: Math.max(0, Number(e.target.value) || 0) })}
                  className="tnum h-9 w-full rounded-md border border-background-300 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400"
                />
              </label>
              <label className="min-w-0">
                <span className="mb-1 block text-[10px] font-medium text-foreground-500">{t('portal.products.eta')}</span>
                <input
                  type="date"
                  value={p.eta}
                  onChange={(e) => patch(p.sku, { eta: e.target.value })}
                  className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-1.5 text-[11px] outline-none focus:border-primary-400"
                />
              </label>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="rounded-lg border border-background-200 bg-background-50 px-3 py-8 text-center text-[12px] text-foreground-500">
            {t('portal.products.empty')}
          </p>
        )}
      </div>

      {toast && (
        <Toast message={toast.message} detail={toast.detail} onClose={() => setToast(null)} />
      )}
    </div>
  );
}